package com.htmlagent.site.components.sections.landing

import com.htmlagent.site.state.ThemeChoice
import com.htmlagent.site.state.ThemePreference
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import com.htmlagent.shared.UiLocale
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.components.widgets.LocaleCode
import com.htmlagent.site.components.widgets.Mark
import com.htmlagent.site.i18n.AppLocale
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.FooterAboutStyle
import com.htmlagent.site.theme.FooterBottomStyle
import com.htmlagent.site.theme.FooterFinePrintStyle
import com.htmlagent.site.theme.FooterLinkStyle
import com.htmlagent.site.theme.FooterLinksStyle
import com.htmlagent.site.theme.FooterNavStyle
import com.htmlagent.site.theme.FooterStyle
import com.htmlagent.site.theme.FooterSwitchesStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.LandingInnerStyle
import com.htmlagent.site.theme.LandingMarkNameStyle
import com.htmlagent.site.theme.LandingMarkTaglineStyle
import com.htmlagent.site.theme.LandingMarkTextStyle
import com.htmlagent.site.theme.LandingMarkStyle
import com.htmlagent.site.theme.LandingMarkTileStyle
import com.htmlagent.site.theme.NavSwitchVariant
import com.htmlagent.site.theme.labelled
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Footer
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * The foot of the page: what the product is, where its sections are, and the two switches.
 *
 * Every link here goes somewhere that exists. A privacy policy and a press page would fill it out
 * to the shape a footer usually has, and both would be doors onto nothing — which is the one
 * thing a page selling a tool that refuses to invent facts cannot do. So the shape follows the
 * content instead: six items are a row, and a row needs no headings over it.
 *
 * The anchors are the same six the bar carries, written the same way. They used to be four of
 * them in a different family at a smaller size, which made the same destination look like two
 * different places depending on which end of the page you read it from.
 *
 * The switches repeat here because the bar drops them below a desktop, and the foot of a long
 * page is where somebody who has read it all is standing.
 */
@Composable
fun LandingFooter() {
    val t = LocalStrings.current

    Footer(LandingInnerStyle.toModifier().then(FooterStyle.toModifier()).toAttrs()) {
        P(FooterAboutStyle.toModifier().toAttrs()) { Text(t.landing.footerAbout) }

        Div(FooterNavStyle.toModifier().toAttrs()) {
            Nav(attrs = { attr("aria-label", t.landing.navMenu) }) {
                Ul(FooterLinksStyle.toModifier().toAttrs()) {
                    FooterAnchor(SECTION_STEPS, t.landing.navHowItWorks)
                    FooterAnchor(SECTION_STATS, t.landing.navInside)
                    FooterAnchor(SECTION_AUDIENCE, t.landing.navAudience)
                    FooterAnchor(SECTION_SAMPLES, t.landing.navSamples)
                    FooterAnchor(SECTION_FAQ, t.landing.navQuestions)
                }
            }

            Switches()
        }

        Div(FooterBottomStyle.toModifier().toAttrs()) {
            Div(LandingMarkStyle.toModifier().toAttrs()) {
                Span(LandingMarkTileStyle.toModifier().toAttrs()) {
                    Mark()
                }
                Span(LandingMarkTextStyle.toModifier().toAttrs()) {
                    Span(LandingMarkNameStyle.toModifier().toAttrs()) { Text(PRODUCT_NAME) }
                    Span(LandingMarkTaglineStyle.toModifier().toAttrs()) { Text(PRODUCT_TAGLINE) }
                }
            }

            // One line and not two rows: the year and who made it are the same small print.
            Div(FooterFinePrintStyle.toModifier().toAttrs()) {
                Span { Text(t.landing.footerCopyright(currentYear())) }
                Span { Text(t.landing.footerMadeWith) }
            }
        }
    }
}

/** One anchor in the row. */
@Composable
private fun FooterAnchor(section: String, label: String) {
    Li {
        A(href = "#$section", attrs = FooterLinkStyle.toModifier().toAttrs()) { Text(label) }
    }
}

/**
 * The language and the color mode, beside the links.
 *
 * They sit outside the list on purpose: they are controls, not places, and inside a `<ul>` a
 * screen reader announced two buttons as a list of one item.
 */
@Composable
private fun Switches() {
    val t = LocalStrings.current

    Div(FooterSwitchesStyle.toModifier().toAttrs()) {
        val locale by AppLocale.current.collectAsState()
        val other = if (locale == UiLocale.SPANISH) UiLocale.ENGLISH else UiLocale.SPANISH
        Button(
            onClick = { AppLocale.set(other) },
            modifier = Modifier.labelled(
                if (other == UiLocale.ENGLISH) t.rail.switchToEnglish else t.rail.switchToSpanish,
            ),
            variant = NavSwitchVariant,
        ) {
            LocaleCode(other)
        }

        val colorMode = ColorMode.current
        Button(
            onClick = { ThemePreference.set(if (colorMode.isLight) ThemeChoice.DARK else ThemeChoice.LIGHT) },
            modifier = Modifier.labelled(
                if (colorMode.isLight) t.rail.switchToDark else t.rail.switchToLight,
            ),
            variant = NavSwitchVariant,
        ) {
            Glyph(if (colorMode.isLight) Icons.Moon else Icons.Sun)
        }
    }
}

/**
 * The year in the copyright, taken from the clock rather than written down.
 *
 * A year typed into a string is wrong every January, and it is the kind of wrong that says
 * nobody has looked at the page since.
 */
private fun currentYear(): Int = js("new Date().getFullYear()") as Int
